/**
 * CR-6521: Bulk OTIF override modal for the OTIF Review queue.
 *
 * Mirrors the PO-Lines bulk pattern (BulkProposeChangeModal): one reason +
 * optional comment entered once, applied identically to every selected line.
 * The combined text is sent as `comments` to the existing per-line
 * override-otif endpoint for each line, so each line still gets its own
 * POChangeLog audit record and the supplier is notified per line.
 *
 * CR-6521 rework: the run is one sequential POST per line, so it reports
 * progress and can be stopped between lines. It used to run to completion with
 * no feedback and no way out, because the Cancel button was disabled for the
 * duration. The dialog still refuses to close mid-run (below) — that part is
 * deliberate and unchanged; what changed is that Cancel now becomes Stop.
 */
import { useState, useEffect } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { ReasonCodeSelect, getEffectiveReason } from './AcknowledgmentSharedUtils';

interface OTIFBulkOverrideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  saving: boolean;
  /** Lines completed so far out of the batch, while a run is in flight. */
  progress?: { done: number; total: number } | null;
  /** Stop the run after the line currently in flight. */
  onStop?: () => void;
  /** Keep the typed reason on close, so a part-failed run can be retried. */
  preserveDraft?: boolean;
  /** Receives the combined reason+comment string to apply to every line. */
  onSubmit: (comments: string) => void;
}

export default function OTIFBulkOverrideModal({
  open, onOpenChange, count, saving, progress, onStop, preserveDraft = false, onSubmit,
}: OTIFBulkOverrideModalProps) {
  const [reasonSelect, setReasonSelect] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [comment, setComment] = useState('');

  const effectiveReason = getEffectiveReason(reasonSelect, customReason);

  useEffect(() => {
    if (!open && !preserveDraft) { setReasonSelect(''); setCustomReason(''); setComment(''); }
  }, [open, preserveDraft]);

  const handleSubmit = () => {
    if (!effectiveReason) return;
    // One reason + comment, applied identically to every line.
    const comments = [effectiveReason, comment.trim()].filter(Boolean).join(' — ');
    onSubmit(comments);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Override OTIF on Selected Lines</DialogTitle>
          <DialogDescription className="sr-only">
            Bulk override OTIF status on the selected PO lines
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <p className="text-sm text-muted-foreground">
            You are about to override the OTIF status on {count} selected line{count > 1 ? 's' : ''},
            marking them as OTIF-passed. The same reason and comment are applied to every line;
            each line keeps its own audit record and the supplier is notified.
          </p>
          <ReasonCodeSelect
            value={reasonSelect}
            onChange={setReasonSelect}
            customValue={customReason}
            onCustomChange={setCustomReason}
            required
            label="Reason for Override"
          />
          {progress && (
            <p role="status" className="text-sm font-medium">
              Overriding line {Math.min(progress.done + 1, progress.total)} of {progress.total}…
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="bulk-otif-comment">Comment (optional)</Label>
            <Textarea
              id="bulk-otif-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Additional context sent to the supplier…"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => (saving ? onStop?.() : onOpenChange(false))}
            disabled={saving && !onStop}
          >
            {saving ? 'Stop' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !effectiveReason} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Override {count} Line{count > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
